import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, Send, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function getSessionId() {
  let id = localStorage.getItem("apex_chat_session");
  if (!id) {
    id = (crypto.randomUUID && crypto.randomUUID()) || `s-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem("apex_chat_session", id);
  }
  return id;
}

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!open || loaded) return;
    fetch(`${API}/api/chat/history/${getSessionId()}`)
      .then((r) => r.json())
      .then((d) => setMessages(d.messages || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [open, loaded]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: text }),
      });
      if (!res.ok || !res.body) throw new Error("chat failed");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const data = part.slice(6);
          if (data === "[DONE]") continue;
          try {
            const j = JSON.parse(data);
            const chunk = j.delta || j.error || "";
            if (chunk) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                  ...copy[copy.length - 1],
                  content: copy[copy.length - 1].content + chunk,
                };
                return copy;
              });
            }
          } catch {
            /* partial frame */
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "Pit radio glitch — please try again, or call +91 98200 78697.",
        };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {open && (
        <div
          className="fixed bottom-24 right-4 md:right-6 z-[60] w-[calc(100vw-2rem)] max-w-sm bg-[#0E0E0E] border border-white/15 shadow-2xl flex flex-col"
          style={{ height: "min(520px, 70vh)" }}
          data-testid="chat-widget-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-black">
            <div>
              <p className="af-mono text-[10px] uppercase tracking-[0.25em] text-[#FF2800]">
                // Pit Radio
              </p>
              <p className="font-semibold text-sm mt-0.5">Apex Assist — AI support</p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="w-8 h-8 inline-flex items-center justify-center border border-white/15 hover:border-white/40 transition-colors"
              data-testid="chat-close-button"
              aria-label="Close chat"
            >
              <X size={14} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.length === 0 && (
              <div className="text-zinc-400 text-sm" data-testid="chat-welcome">
                Radio check! Ask me about frames, prices, the 30% sale, shipping or custom
                builds. For a human, call{" "}
                <span className="text-white af-mono">+91 98200 78697</span>.
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                className={`max-w-[85%] px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "ml-auto bg-[#FF2800] text-white"
                    : "mr-auto bg-white/5 border border-white/10 text-zinc-200"
                }`}
                data-testid={m.role === "user" ? "chat-message-user" : "chat-message-assistant"}
              >
                {m.content || (busy && i === messages.length - 1 ? "…" : "")}
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          <form
            className="p-3 border-t border-white/10 flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your question…"
              className="flex-1 bg-black border border-white/15 px-3 py-2 text-sm focus:outline-none focus:border-[#FF2800] transition-colors"
              data-testid="chat-input"
            />
            <button
              type="submit"
              disabled={busy || !input.trim()}
              className="w-10 h-10 inline-flex items-center justify-center bg-[#FF2800] hover:bg-[#CC2000] disabled:opacity-40 transition-colors"
              data-testid="chat-send-button"
              aria-label="Send"
            >
              <Send size={15} />
            </button>
          </form>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-5 right-4 md:right-6 z-[60] w-14 h-14 rounded-full bg-[#FF2800] hover:bg-[#CC2000] shadow-lg shadow-red-900/40 inline-flex items-center justify-center transition-transform hover:scale-105"
        data-testid="chat-widget-button"
        aria-label="Chat with Apex Assist"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>
    </>
  );
};

export default ChatWidget;
